/**
 * chat.ts — AI pet chat + proactive nudges + dream + timeline
 * (PR-15 split of api/client.ts).
 *
 * Holds the live conversation endpoints between the kid and the active
 * pet's AI persona, plus the "what did the pet do while you were away"
 * timeline. Companion features (greetings, photos, phrases, voice bottle)
 * live in companion.ts to keep this file focused on the chat stream.
 *
 * 调用关系：
 *   - Merged into the unified `api` object via api/client.ts.
 *   - Used by ChatPage (history/send/proactive/dream), HomePage
 *     (chatPetInfo for the speech bubble), TimelinePage (timeline).
 *   - Backend: backend/src/routes/chat.js, dream.js, timeline.js.
 *
 * 关键约束：
 *   - chatSend/chatProactive both pass the active UI language so the AI
 *     replies in the same language; chatDream also forwards lang.
 *   - chatRememberPetLine is the one-way "stash this phrase the kid said
 *     out loud" hook; dedupe is server-side via duplicate=true response.
 */
import { call, getLang } from './_core';
import type { BondGainResponse, PetPersona } from './pets';

export interface ChatMsg {
  id: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  created_at: number;
}

export const chatApi = {
  // Chat with AI pet
  chatHistory(limit = 50) {
    return call<{
      messages: ChatMsg[];
      thread_id: number | null;
      pet?: { id: number; nickname: string; species: string };
    }>(`/api/v1/child/chat/messages?limit=${limit}`);
  },
  chatProactive() {
    return call<{
      message: { id: number; role: 'assistant'; content: string; created_at: number } | null;
      thread_id?: number;
      reason: 'sent' | 'whisper' | 'cooldown' | 'recent_interaction' | 'budget' | 'no_pet' | 'llm_error' | 'empty';
      next_eligible_at?: number;
      last_interaction_at?: number;
    }>('/api/v1/child/chat/proactive', {
      method: 'POST',
      body: JSON.stringify({ lang: getLang() }),
    });
  },
  chatRememberPetLine(content: string) {
    return call<{
      ok: true;
      message: ChatMsg;
      thread_id: number;
      duplicate?: boolean;
    }>('/api/v1/child/chat/remember-pet-line', {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  },
  chatSend(message: string) {
    return call<{
      ok: true; reply: string; reply_id: number; thread_id: number;
      moderated?: boolean; budget_exceeded?: boolean; near_limit?: boolean;
      quick_replies?: string[];
      bond_gain?: BondGainResponse | null;
    }>('/api/v1/child/chat/send', {
      method: 'POST', body: JSON.stringify({ message, lang: getLang() }),
    });
  },
  chatPetInfo() {
    return call<{
      pet: {
        id: number; nickname: string; species: string; breed_name: string;
        persona: PetPersona | null;
      } | null;
    }>('/api/v1/child/chat/pet-info');
  },
  chatDream() {
    return call<{
      sleeping: boolean;
      dream: { content: string; created_at: number } | null;
      reason?: string;
    }>(`/api/v1/child/chat/dream?lang=${getLang()}`);
  },
  timeline() {
    return call<{
      events: Array<{
        id: string; type: string; at: number;
        icon: string; title: string; body?: string | null;
      }>;
    }>(`/api/v1/child/timeline?lang=${getLang()}`);
  },
};
